import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button, Col, Form, Input, Row, Select, Table, Tooltip } from 'antd';
import { DownOutlined, PaperClipOutlined, WarningTwoTone } from '@ant-design/icons';
import { useModal } from '@ebay/nice-modal-react';
import { useAuth } from '@isocel/front-common';
import moment from 'moment';

import { LOCATION_TYPE, PackagingFlowTypeMap } from '../../constants';
import { PackagingFlowDirection, PackagingFlowItem, RoleSlug } from '../../queries/api/types';
import { useStoreByOrganization } from '../../queries/store';
import { getAllPackagingFlow } from '../../queries/api/packaging-flow';
import { fetchPackages } from '../../queries/api/package';
import TableDetailsFormDrawer from '../dashboard/details/TableDetailsDrawer';
import '../receptionsAValider/ReceptionsAValider.less';
import '../dashboard/table/PackagingFlowTable.less';

interface DataType {
    key: string;
    sender: string;
    senderId: string;
    reciever: string;
    recieverId: string;
    date: string;
    unit: string;
    status: string;
    transportVoucher: string;
    flowType: PackagingFlowDirection;
    packages: any[];
    differenceReported: boolean;
    details: string;
    comment: string;
    loadingSession?: any;
    transporter?: any;
    received: boolean;
    originId?: string | null;
    cmr?: string;
    packagingVouchers?: any[];
    packagingVoucherUsed?: any[];
    userInfo?: any;
    attachments?: any[];
    expeditionAt?: string;
    deliveredAt?: string;
    truckNumber?: string;
    orderNumber?: string;
    children?: DataType[];
}

const TRANSFER_TYPE_MAP: Record<string, string> = {
    supplier: LOCATION_TYPE.TRANSPORTEUR,
    palette: LOCATION_TYPE.PARC_PALETTE,
    Warehouse: LOCATION_TYPE.ENTREPOT,
    warehouse: LOCATION_TYPE.ENTREPOT,
    organization: LOCATION_TYPE.SCA,
    provider: LOCATION_TYPE.PRESTATAIRE,
    store: LOCATION_TYPE.MAGASIN,
};

const LITIGE_STATUS_FILTER = ['Non vérifié'];

const toSafeText = (value: unknown): string => (typeof value === 'string' ? value : '');

const LitigesAValider: React.FC = () => {
    const [data, setData] = useState<DataType[]>([]);
    const packagesRef = useRef<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({ page: 1, pageSize: 10 });
    const [total, setTotal] = useState(0);
    const [filterVoucher, setFilterVoucher] = useState('');
    const [filterStoreIds, setFilterStoreIds] = useState<string[]>([]);
    const [pendingVoucher, setPendingVoucher] = useState('');
    const [pendingStoreIds, setPendingStoreIds] = useState<string[]>([]);

    const { user } = useAuth();
    const isOrgAdmin = user?.role?.slug === RoleSlug.orgAdmin;
    const tableDetailsFormDrawer = useModal(TableDetailsFormDrawer);
    const { data: storesData } = useStoreByOrganization(user?.organization?.id ?? '');

    const fetchPackagesData = async () => {
        const response = await fetchPackages();
        packagesRef.current = response;
    };

    const getPackageModelName = useCallback((pkg: any) => {
        if (typeof pkg === 'string') return packagesRef.current.find((item) => item.id === pkg)?.model.name || '';
        return pkg.model?.name || '';
    }, []);

    const getPackageTypeName = useCallback((pkg: any) => {
        if (typeof pkg === 'string') return packagesRef.current.find((item) => item.id === pkg)?.type.name || '';
        return pkg.type?.name || '';
    }, []);

    const getPackageQuantity = useCallback((pkg: PackagingFlowItem) => {
        const receivedQuantity = Number(pkg.goodCount) + Number(pkg.badCount);
        return receivedQuantity === 0 ? Number(pkg.shippedQuantity) : receivedQuantity;
    }, []);

    const fetchData = useCallback(async () => {
        if (!isOrgAdmin) return;

        setLoading(true);

        try {
            const response = await getAllPackagingFlow(
                {
                    page: pagination.page,
                    pageSize: pagination.pageSize,
                    flowType: PackagingFlowDirection.RecivedInStore,
                    sortField: 'createdAt',
                    sortOrder: 'desc',
                    litigeStatus: LITIGE_STATUS_FILTER,
                    ...(filterVoucher ? { transportVoucher: filterVoucher } : {}),
                    ...(filterStoreIds.length > 0 ? { storeIds: filterStoreIds } : {}),
                },
                { organization: { id: user?.organization?.id ?? '' } }
            );

            // Client-side filter matching dashboard pattern exactly
            const filtered = response.items;

            const rows: DataType[] = filtered.map((flow) => {
                const flowTypeMap = PackagingFlowTypeMap.get(flow.flowType);
                let recieverType = flowTypeMap?.recipientType || LOCATION_TYPE.UNKNOWN;

                if (flow.flowType === PackagingFlowDirection.Transfer) {
                    recieverType = TRANSFER_TYPE_MAP[flow.recieverType || ''] || recieverType;
                } else if (
                    flow.flowType === PackagingFlowDirection.Increase ||
                    flow.flowType === PackagingFlowDirection.Decrease
                ) {
                    recieverType = TRANSFER_TYPE_MAP[flow.recieverType || ''] || recieverType;
                }

                const allPackages = flow.packages.map((pkgItem: PackagingFlowItem) => ({
                    ...pkgItem,
                    model: getPackageModelName(pkgItem.package),
                    type: getPackageTypeName(pkgItem.package),
                }));

                const unitCount = allPackages.reduce((sum: number, pkgItem: PackagingFlowItem) => {
                    return sum + getPackageQuantity(pkgItem);
                }, 0);

                const hasNonVerifiedLitige = allPackages.some((pkg) => {
                    const status = pkg.statusDifferenceReported;
                    return status && LITIGE_STATUS_FILTER.includes(status);
                });

                return {
                    key: flow._id || '',
                    sender: `Entrepôt - ${toSafeText(flow.sender?.name)}`,
                    senderId: flow.sender?._id || '',
                    reciever: `${toSafeText(recieverType)} - ${
                        toSafeText(flow.recipient?.name) || LOCATION_TYPE.UNKNOWN
                    }`,
                    recieverId: flow.recipient?._id || '',
                    date: flow.expeditionAt
                        ? moment(flow.expeditionAt).format('DD/MM/YYYY HH:mm')
                        : flow.deliveredAt
                        ? moment(flow.deliveredAt).format('DD/MM/YYYY HH:mm')
                        : moment(flow.createdAt).format('DD/MM/YYYY HH:mm'),
                    unit: unitCount.toString(),
                    status: flowTypeMap?.label || LOCATION_TYPE.UNKNOWN,
                    transportVoucher: flow.transportVoucher || '',
                    flowType: flow.flowType,
                    packages: allPackages,
                    differenceReported: flow.differenceReported || hasNonVerifiedLitige,
                    details: '',
                    comment: flow.comment || '',
                    loadingSession: flow.loadingSession,
                    transporter: flow.transporter || null,
                    received: flow.received || false,
                    originId: flow.originId || null,
                    cmr: flow.cmr || '',
                    packagingVouchers: flow.packagingVouchers || [],
                    packagingVoucherUsed: flow.packagingVoucherUsed || [],
                    userInfo: flow.user,
                    attachments: flow.attachments || [],
                    expeditionAt: flow.expeditionAt || '',
                    deliveredAt: flow.deliveredAt || '',
                    truckNumber: flow.truckNumber || '',
                    orderNumber: flow.orderNumber || '',
                    children: allPackages.map((pkgItem: any) => ({
                        key: pkgItem._id || Math.random().toString(),
                        sender: '',
                        senderId: '',
                        reciever: '',
                        recieverId: '',
                        date: '',
                        unit: '',
                        status: '',
                        transportVoucher: '',
                        flowType: flow.flowType,
                        packages: [pkgItem],
                        differenceReported: false,
                        details: '',
                        comment: '',
                        received: false,
                    })),
                };
            });

            setData(rows);
            setTotal(response.totalCount ?? filtered.length);
        } catch (error) {
            console.error('Error fetching litiges a valider:', error);
        } finally {
            setLoading(false);
        }
    }, [
        filterStoreIds,
        filterVoucher,
        getPackageModelName,
        getPackageQuantity,
        getPackageTypeName,
        isOrgAdmin,
        pagination,
        user,
    ]);

    useEffect(() => {
        fetchPackagesData();
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleDetailsClick = (record: DataType) => {
        tableDetailsFormDrawer.show({ data: record, refreshDashboard: fetchData });
    };

    if (!isOrgAdmin) return null;

    const columns = [
        {
            title: 'Émetteur',
            dataIndex: 'sender',
            key: 'sender',
        },
        {
            title: 'Destinataire',
            dataIndex: 'reciever',
            key: 'reciever',
        },
        {
            title: 'Date',
            dataIndex: 'date',
            key: 'date',
            sorter: true,
        },
        {
            title: 'Unité',
            dataIndex: 'unit',
            key: 'unit',
            sorter: true,
            render: (_: string, record: DataType) => (
                <>
                    {record.children && <div>{record.unit}</div>}
                    {!record.children &&
                        record.packages?.map(
                            (
                                pkg: PackagingFlowItem & { _id?: string; model?: string; type?: string },
                                index: number
                            ) => (
                                <div key={`${pkg._id || index}-${index}`}>
                                    {getPackageQuantity(pkg)} -{' '}
                                    {toSafeText(pkg.type) === toSafeText(pkg.model)
                                        ? toSafeText(pkg.type)
                                        : `${toSafeText(pkg.type)} ${toSafeText(pkg.model)}`}
                                </div>
                            )
                        )}
                </>
            ),
        },
        {
            title: 'Statut',
            dataIndex: 'flowType',
            key: 'flowType',
            render: (flowType: number) => {
                const flowTypeMap = PackagingFlowTypeMap.get(flowType);
                return <div className={`tag ${flowTypeMap?.tagClass || ''}`}>{flowTypeMap?.label || ''}</div>;
            },
        },
        {
            title: 'Bon de transport',
            dataIndex: 'transportVoucher',
            key: 'transportVoucher',
            render: (_: string, record: DataType) => {
                if (!record.children) return null;
                return <div>{record.transportVoucher}</div>;
            },
        },
        {
            title: 'Action',
            key: 'action',
            render: (_: unknown, record: DataType) => {
                if (!record.children) return null;
                return (
                    <div style={{ display: 'flex', gap: 8 }}>
                        <Button size="small" type="primary" onClick={() => handleDetailsClick(record)}>
                            Détails
                        </Button>
                    </div>
                );
            },
        },
        {
            title: 'Alerte écart',
            dataIndex: 'differenceReported',
            key: 'differenceReported',
            render: (_: unknown, record: DataType) => {
                const icons = [];
                if (record.differenceReported) {
                    icons.push(
                        <Tooltip
                            key="differenceReported"
                            title="La réception déclarée est différente de l'expédition. Veuillez cliquer sur 'Détails' pour en savoir plus"
                        >
                            <WarningTwoTone twoToneColor="#f08e02" className="px-16" />
                        </Tooltip>
                    );
                    if (record.attachments && record.attachments.length > 0) {
                        icons.push(<PaperClipOutlined key="attachments" className="text-blue" />);
                    }
                }
                return <div>{icons}</div>;
            },
        },
    ];

    const applyFilters = () => {
        setFilterVoucher(pendingVoucher);
        setFilterStoreIds(pendingStoreIds);
        setPagination((current) => ({ ...current, page: 1 }));
    };

    return (
        <div className="receptions-a-valider-container">
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16 }}>
                <h1 className="receptions-a-valider-title" style={{ marginBottom: 0 }}>
                    Litiges à valider{total > 0 && <span className="count-badge">{total}</span>}
                </h1>
            </div>
            <Form layout="vertical">
                <Row gutter={16} align="bottom">
                    <Col xs={24} sm={24} md={12} lg={8}>
                        <Form.Item label="Bon de transport" className="bold-label">
                            <Input
                                placeholder="Bon de transport"
                                value={pendingVoucher}
                                onChange={(e) => setPendingVoucher(e.target.value)}
                                allowClear
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={24} md={12} lg={8}>
                        <Form.Item label="Magasin" className="bold-label">
                            <Select
                                mode="multiple"
                                placeholder="Magasin"
                                value={pendingStoreIds}
                                onChange={(values) => setPendingStoreIds(values)}
                                allowClear
                                showSearch
                                optionFilterProp="label"
                                style={{ width: '100%' }}
                                options={(storesData || []).map((store) => ({
                                    label: `${store.code} - ${store.name}`,
                                    value: store.id,
                                }))}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={24} md={12} lg={4}>
                        <Form.Item>
                            <Button type="primary" onClick={applyFilters}>
                                Appliquer les filtres
                            </Button>
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
            <Table
                size="small"
                columns={columns}
                dataSource={data}
                loading={loading}
                pagination={{
                    current: pagination.page,
                    pageSize: pagination.pageSize,
                    total,
                    showSizeChanger: true,
                    position: ['bottomRight'],
                    onChange: (page, pageSize) => setPagination({ page, pageSize: pageSize || pagination.pageSize }),
                }}
                expandable={{
                    expandIcon: ({ expanded, onExpand, record }) => {
                        if (!record.children) return <></>;
                        return (
                            <DownOutlined
                                className="expand-icon"
                                onClick={(e) => onExpand(record, e)}
                                style={{ transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
                            />
                        );
                    },
                }}
            />
        </div>
    );
};

export default LitigesAValider;
